/**
 * Zoho Books integration hooks
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

export interface ZohoStatus {
  connected: boolean;
  org_name?: string;
  org_id?: string;
  data_center?: string;
  expires_at?: string;
  connected_at?: string;
}

export interface ZohoOrganization {
  organization_id: string;
  name: string;
  currency_code: string;
  country: string;
}

export interface ZohoCategory {
  id: string;
  code: string;
  name_en: string;
  zoho_account_id: string | null;
  zoho_account_name: string | null;
}

export interface ZohoAccount {
  zoho_account_id: string;
  account_name: string;
  account_code: string | null;
  account_type: string | null;
}

// ─── Status ─────────────────────────────────────────────────────
export function useZohoStatus() {
  return useQuery({
    queryKey: ['zoho', 'status'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ZohoStatus }>('/api/v1/zoho/auth/status');
      return data.data;
    },
    refetchInterval: 30_000, // poll every 30s
  });
}

// ─── Connect (returns auth URL) ─────────────────────────────────
export function useZohoConnect() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<{ data: { auth_url: string; state: string } }>(
        '/api/v1/zoho/auth/connect'
      );
      return data.data;
    },
  });
}

// ─── Disconnect ─────────────────────────────────────────────────
export function useZohoDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ data: { ok: boolean } }>(
        '/api/v1/zoho/auth/disconnect',
        {}
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['zoho'] });
    },
  });
}

// ─── Test connection ────────────────────────────────────────────
export function useZohoTest() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{
        data: { ok: boolean; organization: ZohoOrganization | null };
      }>('/api/v1/zoho/test', {});
      return data.data;
    },
  });
}

// ─── Sync chart of accounts ─────────────────────────────────────
export function useZohoSyncAccounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ data: { synced: number } }>(
        '/api/v1/zoho/sync/accounts',
        {}
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['zoho', 'accounts'] });
    },
  });
}

// ─── Sync vendors ───────────────────────────────────────────────
export function useZohoSyncVendors() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ data: { synced: number } }>(
        '/api/v1/zoho/sync/vendors',
        {}
      );
      return data.data;
    },
  });
}

// ─── Categories with mappings ───────────────────────────────────
export function useZohoCategories() {
  return useQuery({
    queryKey: ['zoho', 'categories'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ZohoCategory[] }>('/api/v1/zoho/categories');
      return data.data;
    },
  });
}

// ─── Available Zoho expense accounts ────────────────────────────
export function useZohoAccounts() {
  return useQuery({
    queryKey: ['zoho', 'accounts'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ZohoAccount[] }>('/api/v1/zoho/accounts');
      return data.data;
    },
  });
}

// ─── Save category mappings ─────────────────────────────────────
export function useZohoSaveMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mappings: { category_id: string; zoho_account_id: string | null }[]) => {
      const { data } = await apiClient.post<{ data: { saved: number } }>(
        '/api/v1/zoho/category-mappings',
        { mappings }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['zoho', 'categories'] });
    },
  });
}
